// Поле примечаний табельщика под сеткой табеля.
//
// ## Откуда взялось
//
// Заказчик 21.09.2026, дословно и со стрелкой на снимке: «нужно поле под графиками для
// комментариев, т.к. табельщик пишет различные примечания по каждому объекту».
//
// Он ведёт их УЖЕ — на бумаге и в переписке, — и они объясняют то, чего из цифр не видно:
// «Иванов уехал 12-го по семейным, справка у бригадира», «26-го смену закрыли на 18 часов,
// авария на скважине». Через месяц, когда финотдел спросит, откуда в табеле недобор, отвечать
// будет нечем: разбор превращается в обзвон.
//
// ## Почему сохранение по уходу из поля, а не по каждой букве и не по кнопке
//
// Запрос на нажатие — это запрос на букву и гонка двух текстов: табельщиков у объекта бывает
// двое. Кнопка — это кнопка, которую забудут нажать, и текст пропадёт при переключении месяца.
// Уход из поля закрывает оба случая: дописал, ушёл — сохранилось.
//
// Молча при этом сохранять нельзя: «сохранилось или нет» — первый вопрос к полю, которое само
// себя записывает. Поэтому под ним стоит «Сохранено 21.09.2026 в 14:32, Петров», и та же
// подпись отвечает на второй вопрос — «кто это писал» — через месяц, когда спросят.
//
// ## Почему после закрытия периода поле только читается
//
// Примечание — часть закрытого документа: правка после подписи означала бы, что подписанное
// можно менять задним числом. Написанное при этом остаётся на экране обычным текстом, а не
// серым выключенным полем: выключенное поле выглядит сломанным, а причина сказана рядом.

import { useEffect, useRef, useState } from 'react';

import type { GridOut } from '@/shared/api/endpoints/timesheet';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { TextArea } from '@/shared/ui/text-area';

import type { useSetTimesheetNote } from '../queries';

/** «Сохранено 21.09.2026 в 14:32, Петров». Пусто — примечания ещё не было. */
function подписьПравки(когда: string | null, кто: string | null): string | null {
  const момент = parseMoment(когда);
  if (момент === null || кто === null) {
    return null;
  }
  return `Сохранено ${formatDateDMY(момент)} в ${formatTimeHM(момент)}, ${кто}`;
}

export function ПримечанияОбъекта({
  данные,
  правится,
  запретПричина,
  запись,
}: {
  данные: GridOut;
  правится: boolean;
  /** Почему править нельзя. Пусто — можно. */
  запретПричина: string | null;
  запись: ReturnType<typeof useSetTimesheetNote>;
}) {
  const сохранённое = данные.period.note ?? '';
  const [текст, setТекст] = useState(сохранённое);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const прежнее = useRef(сохранённое);

  // Чужая правка и переключение объекта приезжают одним путём — новой сеткой. Затирать ею
  // НЕДОПИСАННОЕ нельзя: человек мог набирать текст, пока приехал ответ на соседнее действие.
  // Подменяем, только когда сохранённое правда сменилось.
  useEffect(() => {
    if (прежнее.current !== сохранённое) {
      прежнее.current = сохранённое;
      setТекст(сохранённое);
    }
  }, [сохранённое]);

  function сохранить() {
    const очищенный = текст.trim();
    if (очищенный === сохранённое.trim()) {
      return;
    }
    if (!правится) {
      setЗапрет(
        запретПричина ??
          'Примечания ведёт табельщик или финансовый отдел. Попросите их либо администратора выдать вам право.',
      );
      setТекст(сохранённое);
      return;
    }
    setЗапрет(null);
    // Пустой текст СНИМАЕТ примечание: стереть написанное должно быть так же просто, как
    // написать, иначе первая же опечатка остаётся навсегда.
    запись.mutate({ note: очищенный === '' ? null : очищенный, version: данные.period.version });
  }

  const закрыт = данные.period.status === 'closed';
  const подпись = подписьПравки(данные.period.note_at, данные.period.note_author);

  if (закрыт) {
    return (
      <div className='flex flex-col gap-1 border-t border-stroke-soft-200 px-5 py-3 узко:px-4'>
        <span className='text-label-sm text-text-strong-950'>Примечания по объекту</span>
        <p className='whitespace-pre-wrap text-paragraph-sm text-text-strong-950'>
          {сохранённое === '' ? 'За этот месяц примечаний не оставляли.' : сохранённое}
        </p>
        <Hint.Root>
          <Hint.Icon />
          {подпись === null
            ? 'Период закрыт: примечание — часть закрытого документа и правкой не меняется.'
            : `${подпись}. Период закрыт: примечание — часть закрытого документа и правкой не меняется.`}
        </Hint.Root>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 px-5 py-3 узко:px-4'>
      <TextArea
        id='примечания-табеля'
        label='Примечания по объекту'
        value={текст}
        onChange={setТекст}
        onBlur={сохранить}
        rows={3}
        maxLength={4000}
        placeholder='Что нужно помнить про этот месяц. Например: 26-го смену закрыли на 18 часов, авария на скважине, справка у бригадира.'
        hint={
          запись.isPending
            ? 'Сохраняем…'
            : (подпись ??
              'Сохраняется само, когда уйдёте из поля. Это видит каждый, кто откроет табель за этот месяц.')
        }
      />
      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {запись.isError && <ErrorAlert error={запись.error as never} />}
    </div>
  );
}
